import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Send, CheckCircle, XCircle, Clock, Edit2, Trash2,
  ChevronDown, AlertTriangle, Mail, RefreshCw, Bell
} from 'lucide-react';

const STAT_TONES = { slate: 'bg-slate-50 border-slate-200 text-slate-800', amber: 'bg-amber-50 border-amber-200 text-amber-800', blue: 'bg-sky-50 border-sky-200 text-sky-800', red: 'bg-red-50 border-red-200 text-red-700' };

const API = process.env.REACT_APP_BACKEND_URL + '/api/ratesheets';

export default function RatesheetManager() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, create, edit, detail
  const [selected, setSelected] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [sheetsR, statsR] = await Promise.all([
        axios.get(`${API}/list`, { params: filter !== 'all' ? { status: filter } : {} }),
        axios.get(`${API}/stats/summary`),
      ]);
      setSheets(sheetsR.data);
      setStats(statsR.data);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!defaults) axios.get(`${API}/defaults`).then(r => setDefaults(r.data)).catch(() => {});
  }, [defaults]);

  const statusColors = {
    draft: 'bg-slate-200 text-slate-500 border-slate-500/30',
    pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    expired: 'bg-red-50 text-red-600 border-red-200',
  };

  if (view === 'create' || view === 'edit') {
    return <RatesheetForm defaults={defaults} existing={view === 'edit' ? selected : null}
      onSave={() => { setView('list'); load(); }} onCancel={() => setView('list')} />;
  }

  if (view === 'detail' && selected) {
    return <RatesheetDetail sheet={selected} onBack={() => { setView('list'); load(); }} onUpdate={load} />;
  }

  return (
    <div className="space-y-4" data-testid="ratesheet-manager">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'slate' },
            { label: 'Pending Approval', value: stats.pending_approval, color: 'amber' },
            { label: 'Sent & Active', value: stats.sent_active, color: 'blue' },
            { label: 'Follow-ups Due', value: stats.followups_due, color: stats.followups_due > 0 ? 'red' : 'slate' },
          ].map(s => (
            <div key={s.label} className={`p-3 rounded-xl border text-center ${STAT_TONES[s.color]}`}>
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {['all', 'draft', 'pending_approval', 'approved', 'sent'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:text-slate-900'}`}>
              {f === 'all' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </button>
          ))}
        </div>
        <button onClick={() => setView('create')} data-testid="create-ratesheet-btn"
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Ratesheet
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading...</div>
      ) : sheets.length === 0 ? (
        <div className="text-center py-12 text-slate-500">No ratesheets found</div>
      ) : (
        <div className="space-y-2">
          {sheets.map(s => (
            <div key={s.id} onClick={() => { setSelected(s); setView('detail'); }}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:bg-slate-100 cursor-pointer transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-blue-700 font-bold">{s.quotation_number}</span>
                    <span className="text-slate-900 font-semibold">{s.customer_name || 'Untitled'}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${statusColors[s.status]}`}>{s.status.replace('_', ' ')}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {s.attention && `Attn: ${s.attention} — `}
                    Created by {s.created_by} on {new Date(s.created_at).toLocaleDateString()}
                    {s.next_followup_date && ` — Follow-up: ${s.next_followup_date}`}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Ratesheet Form (Create/Edit) ──────────────────────────────────── */
function RatesheetForm({ defaults, existing, onSave, onCancel }) {
  const [form, setForm] = useState(existing || {
    customer_name: '', customer_address: '', customer_city: '', attention: '',
    rates: defaults?.rates || [],
    terms: (defaults?.terms || []).map(t => t.text),
    purchase_terms: (defaults?.purchase_terms || []).map(t => t.text),
    custom_terms: [],
    pm_name: '', pm_email: '', pm_phone: '', pm_cell: '',
    rate_effective_from: '2025-11-01', rate_effective_to: '2026-11-01',
  });
  const [saving, setSaving] = useState(false);
  const [newTerm, setNewTerm] = useState('');

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateRate = (idx, field, value) => {
    const rates = [...form.rates];
    rates[idx] = { ...rates[idx], [field]: parseFloat(value) || 0 };
    updateField('rates', rates);
  };
  const toggleTerm = (text) => {
    const terms = form.terms.includes(text) ? form.terms.filter(t => t !== text) : [...form.terms, text];
    updateField('terms', terms);
  };
  const addCustomTerm = () => {
    if (!newTerm.trim()) return;
    updateField('custom_terms', [...form.custom_terms, newTerm.trim()]);
    setNewTerm('');
  };

  const save = async () => {
    if (!form.customer_name) { toast.error('Customer name required'); return; }
    setSaving(true);
    try {
      if (existing) {
        await axios.put(`${API}/${existing.id}`, form);
      } else {
        await axios.post(`${API}/create`, form);
      }
      toast.success(existing ? 'Ratesheet updated' : 'Ratesheet created');
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900">{existing ? 'Edit' : 'New'} Ratesheet</h2>
        <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900">Cancel</button>
      </div>

      {/* Customer Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Customer Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input value={form.customer_name} onChange={e => updateField('customer_name', e.target.value)} placeholder="Company Name *"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
          <input value={form.attention} onChange={e => updateField('attention', e.target.value)} placeholder="Attention: Contact Name"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
          <input value={form.customer_address} onChange={e => updateField('customer_address', e.target.value)} placeholder="Address"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
          <input value={form.customer_city} onChange={e => updateField('customer_city', e.target.value)} placeholder="City, Province, Postal Code"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
        </div>
      </div>

      {/* Rates */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Labour Rates</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 text-left">Trade</th>
                <th className="py-2 text-center">Regular Time</th>
                <th className="py-2 text-center">Shift</th>
                <th className="py-2 text-center">Overtime</th>
              </tr>
            </thead>
            <tbody>
              {form.rates.map((r, i) => (
                <tr key={i} className="border-b border-slate-200">
                  <td className="py-2 text-slate-900 font-medium">{r.trade}</td>
                  <td className="py-2"><input type="number" step="0.01" value={r.regular} onChange={e => updateRate(i, 'regular', e.target.value)}
                    className="w-24 mx-auto block px-2 py-1 bg-white border border-slate-300 rounded text-slate-900 text-center outline-none" /></td>
                  <td className="py-2"><input type="number" step="0.01" value={r.shift} onChange={e => updateRate(i, 'shift', e.target.value)}
                    className="w-24 mx-auto block px-2 py-1 bg-white border border-slate-300 rounded text-slate-900 text-center outline-none" /></td>
                  <td className="py-2"><input type="number" step="0.01" value={r.overtime} onChange={e => updateRate(i, 'overtime', e.target.value)}
                    className="w-24 mx-auto block px-2 py-1 bg-white border border-slate-300 rounded text-slate-900 text-center outline-none" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Terms */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Clarifications & Terms</h3>
        <div className="space-y-1">
          {(defaults?.terms || []).map((t, i) => (
            <label key={i} className="flex items-start gap-2 py-1 cursor-pointer">
              <input type="checkbox" checked={form.terms.includes(t.text)} onChange={() => toggleTerm(t.text)}
                className="mt-1 rounded border-slate-500" />
              <span className="text-sm text-slate-700">{t.text}</span>
            </label>
          ))}
        </div>
        {form.custom_terms.length > 0 && (
          <div className="space-y-1 pt-2 border-t border-slate-200">
            <p className="text-xs text-slate-500 font-semibold">Custom Terms:</p>
            {form.custom_terms.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-sm text-slate-700 flex-1">{t}</span>
                <button onClick={() => updateField('custom_terms', form.custom_terms.filter((_, j) => j !== i))}
                  className="text-red-600 hover:text-red-700"><XCircle className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <input value={newTerm} onChange={e => setNewTerm(e.target.value)} placeholder="Add custom term..."
            onKeyDown={e => e.key === 'Enter' && addCustomTerm()}
            className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
          <button onClick={addCustomTerm} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-sm">Add</button>
        </div>
      </div>

      {/* Purchase Terms */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Purchases & Equipment Rentals</h3>
        <div className="space-y-1">
          {(defaults?.purchase_terms || []).map((t, i) => (
            <label key={i} className="flex items-start gap-2 py-1 cursor-pointer">
              <input type="checkbox" checked={form.purchase_terms.includes(t.text)} onChange={() => {
                const pts = form.purchase_terms.includes(t.text) ? form.purchase_terms.filter(x => x !== t.text) : [...form.purchase_terms, t.text];
                updateField('purchase_terms', pts);
              }} className="mt-1 rounded border-slate-500" />
              <span className="text-sm text-slate-700">{t.text}</span>
            </label>
          ))}
        </div>
      </div>

      {/* PM Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Project Manager / Signatory</h3>
        <div className="flex gap-2 mb-3">
          {[
            { name: 'Justin Koleszar', email: 'JustinK@365ISI.com', phone: '519-448-4800 ext. 430', cell: '548-328-1711' },
            { name: 'Carl Laput', email: 'CarlL@365isi.com', phone: '519-448-4800 ext. 425', cell: '519-802-4839' },
            { name: 'Jeff Henhoeffer', email: 'JeffH@365isi.com', phone: '519-448-4800', cell: '' },
          ].map(pm => (
            <button key={pm.name} onClick={() => { updateField('pm_name', pm.name); updateField('pm_email', pm.email); updateField('pm_phone', pm.phone); updateField('pm_cell', pm.cell); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${form.pm_name === pm.name ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {pm.name.split(' ')[0]}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input value={form.pm_name} onChange={e => updateField('pm_name', e.target.value)} placeholder="Name"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
          <input value={form.pm_email} onChange={e => updateField('pm_email', e.target.value)} placeholder="Email"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
          <input value={form.pm_phone} onChange={e => updateField('pm_phone', e.target.value)} placeholder="Office Phone"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
          <input value={form.pm_cell} onChange={e => updateField('pm_cell', e.target.value)} placeholder="Cell Phone"
            className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 outline-none" />
        </div>
      </div>

      {/* Save */}
      <div className="flex gap-3">
        <button onClick={save} disabled={saving}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-xl font-semibold">
          {saving ? 'Saving...' : existing ? 'Update Ratesheet' : 'Create Ratesheet'}
        </button>
        <button onClick={onCancel} className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-xl font-medium">Cancel</button>
      </div>
    </div>
  );
}

/* ── Ratesheet Detail View ──────────────────────────────────────────── */
function RatesheetDetail({ sheet, onBack, onUpdate }) {
  const [s, setS] = useState(sheet);
  const [showSkip, setShowSkip] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/${s.id}`);
      setS(r.data);
    } catch {}
  };

  const submitForApproval = async () => {
    try {
      await axios.post(`${API}/${s.id}/submit`);
      toast.success('Submitted for approval');
      refresh(); onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const approve = async () => {
    try {
      await axios.post(`${API}/${s.id}/approve`, { notes: approvalNotes });
      toast.success('Ratesheet approved');
      refresh(); onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const reject = async () => {
    try {
      await axios.post(`${API}/${s.id}/reject`, { notes: approvalNotes || 'Returned for revision' });
      toast.success('Returned to draft');
      refresh(); onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);

  const sendRatesheet = async () => {
    if (!sendEmail) { toast.error('Enter customer email'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/${s.id}/send-email`, { email: sendEmail });
      toast.success(`Emailed to ${r.data.emailed_to}${r.data.cc?.length ? ' (CC: ' + r.data.cc.join(', ') + ')' : ''}`);
      setSendEmail('');
      refresh(); onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const followupDone = async () => {
    try {
      await axios.post(`${API}/${s.id}/followup-done`, { notes: 'Contact made' });
      toast.success('Follow-up completed — next in 30 days');
      refresh(); onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const followupSkip = async () => {
    if (!skipReason) { toast.error('Select a reason'); return; }
    try {
      await axios.post(`${API}/${s.id}/followup-skip`, { reason: skipReason });
      toast.success('Follow-up skipped');
      setShowSkip(false);
      refresh(); onUpdate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const printRatesheet = () => {
    const pw = window.open('', '_blank');
    if (!pw) return;
    const rates = s.rates || [];
    const terms = s.terms || [];
    const purchaseTerms = s.purchase_terms || [];
    const customTerms = s.custom_terms || [];

    const LETTERHEAD_URL = window.location.origin + '/365-letterhead.jpg';

    let html = `<html><head><title>365 Industrial - Ratesheet ${s.quotation_number}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:30px 50px;color:#000;font-size:12px;max-width:850px;margin:0 auto}
        .letterhead{text-align:center;margin-bottom:25px}
        .letterhead img{width:100%;max-width:750px;height:auto}
        .meta{display:flex;justify-content:space-between;margin-bottom:20px;font-size:12px}
        .customer{margin-bottom:15px;line-height:1.6}
        .customer strong{font-size:13px}
        .re-line{font-weight:bold;text-decoration:underline;margin:15px 0 8px}
        table.rates{width:80%;border-collapse:collapse;margin:12px 0 5px}
        table.rates th{padding:4px 12px;text-align:left;font-weight:bold;border-bottom:1px solid #000}
        table.rates td{padding:3px 12px}
        .rates-note{font-size:10px;color:#555;margin:2px 0 15px}
        .section-title{font-weight:bold;text-decoration:underline;margin:18px 0 6px;font-size:12px}
        ul{margin:4px 0;padding-left:24px}
        li{margin:5px 0;font-size:11px;line-height:1.5}
        li::marker{content:'\\25A1  '}
        .closing{margin-top:25px;font-size:11px;line-height:1.6}
        .signature{margin-top:30px;font-size:11px;line-height:1.8}
        @media print{body{padding:15px 40px}@page{margin:1cm}}
      </style></head><body>
      <div class="letterhead">
        <img src="${LETTERHEAD_URL}" alt="365 Industrial Solutions"/>
      </div>
      <div class="meta">
        <span>${new Date(s.date || s.created_at).toLocaleDateString('en-US', {month:'long', day:'numeric', year:'numeric'})}</span>
        <span>Quotation Number: ${s.quotation_number}</span>
      </div>
      <div class="customer">
        <strong>${s.customer_name}</strong><br/>
        ${s.customer_address ? s.customer_address + '<br/>' : ''}
        ${s.customer_city ? s.customer_city : ''}
        ${s.attention ? '<br/><br/>Attn: ' + s.attention + ',' : ''}
      </div>
      <p class="re-line">RE: 365 Industrial Solutions Inc. (Labour Rates)</p>
      <p>We are pleased to submit our Time & Material labor rates for the following year.</p>
      <table class="rates">
        <tr><th>Trade</th><th>Regular Time</th><th>Shift</th><th>Overtime</th></tr>
        <tr><td colspan="4" style="padding:0"><hr style="margin:0;border:none;border-top:1px solid #000"/></td></tr>
        <tr><td><strong>Millwright</strong></td><td></td><td></td><td></td></tr>`;
    for (const r of rates) {
      const indent = r.trade.startsWith('Millwright') ? '&nbsp;&nbsp;&nbsp;' + r.trade.replace('Millwright ','') : r.trade;
      html += `<tr><td>${indent}</td><td>$${r.regular.toFixed(2)}/hr.</td><td>$${r.shift.toFixed(2)}/hr.</td><td>$${r.overtime.toFixed(2)}/hr.</td></tr>`;
    }
    html += `</table>
      <p class="rates-note">*Prices effective ${s.rate_effective_from || 'Nov 1, 2025'} – ${s.rate_effective_to || 'Nov 1, 2026'}.</p>
      <p class="section-title">Clarifications:</p><ul>`;
    for (const t of terms) html += `<li>${t}</li>`;
    for (const t of customTerms) html += `<li>${t}</li>`;
    html += `</ul>
      <p class="section-title">Purchases & Equipment/Equipment Rentals:</p><ul>`;
    for (const t of purchaseTerms) html += `<li>${t}</li>`;
    html += `</ul>
      <p class="closing">We thank you for the opportunity and look forward to working with you and your company in the near future.</p>
      <div class="signature">`;
    const ORIGIN = window.location.origin;
    const pmLower = (s.pm_name || '').toLowerCase();
    if (pmLower.includes('justin')) {
      html += `<img src="${ORIGIN}/sig-justin.png" style="max-height:200px;margin:10px 0" alt=""/>`;
    } else if (pmLower.includes('carl')) {
      html += `<img src="${ORIGIN}/sig-carl.png" style="max-height:200px;margin:10px 0" alt=""/>`;
    } else if (pmLower.includes('jeff')) {
      html += `<img src="${ORIGIN}/sig-jeff.png" style="max-height:200px;margin:10px 0" alt=""/>`;
    }
    html += `</div></body></html>`;
    pw.document.write(html); pw.document.close(); pw.print();
  };

  const statusColors = {
    draft: 'bg-slate-200 text-slate-500', pending_approval: 'bg-amber-50 text-amber-700',
    approved: 'bg-emerald-50 text-emerald-700', sent: 'bg-blue-50 text-blue-700', expired: 'bg-red-50 text-red-600',
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="ratesheet-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-slate-500 hover:text-slate-900 text-sm">Back</button>
          <h2 className="text-xl font-bold text-slate-900">{s.quotation_number}</h2>
          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusColors[s.status]}`}>{s.status.replace('_', ' ')}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={printRatesheet} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-xs flex items-center gap-1">
            <FileText className="w-3 h-3" /> Print / PDF
          </button>
        </div>
      </div>

      {/* Customer + Rates Preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex justify-between mb-3">
          <div>
            <p className="text-slate-900 font-bold text-lg">{s.customer_name}</p>
            {s.customer_address && <p className="text-sm text-slate-500">{s.customer_address}</p>}
            {s.customer_city && <p className="text-sm text-slate-500">{s.customer_city}</p>}
            {s.attention && <p className="text-sm text-slate-500 mt-1">Attn: {s.attention}</p>}
          </div>
          <div className="text-right text-sm text-slate-500">
            <p>Created by {s.created_by}</p>
            <p>{new Date(s.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <table className="w-full text-sm mt-3">
          <thead><tr className="text-xs text-slate-500 border-b border-slate-200">
            <th className="py-1 text-left">Trade</th><th className="py-1 text-center">Regular</th><th className="py-1 text-center">Shift</th><th className="py-1 text-center">Overtime</th>
          </tr></thead>
          <tbody>
            {(s.rates || []).map((r, i) => (
              <tr key={i} className="border-b border-slate-200">
                <td className="py-1 text-slate-900 font-medium">{r.trade}</td>
                <td className="py-1 text-center text-slate-900">${r.regular}/hr</td>
                <td className="py-1 text-center text-slate-900">${r.shift}/hr</td>
                <td className="py-1 text-center text-slate-900">${r.overtime}/hr</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Workflow Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Actions</h3>
        <div className="flex flex-wrap gap-2">
          {s.status === 'draft' && (
            <button onClick={submitForApproval} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
              <Send className="w-4 h-4" /> Submit for Approval
            </button>
          )}
          {s.status === 'pending_approval' && (
            <>
              <input value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} placeholder="Approval notes (optional)"
                className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
              <button onClick={approve} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Approve
              </button>
              <button onClick={reject} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                <XCircle className="w-4 h-4" /> Return for Revision
              </button>
            </>
          )}
          {s.status === 'approved' && (
            <div className="flex items-center gap-2 w-full">
              <input value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="Customer email address"
                className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none" />
              <button onClick={sendRatesheet} disabled={sending}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-lg text-sm font-medium flex items-center gap-2 whitespace-nowrap">
                <Mail className="w-4 h-4" /> {sending ? 'Sending...' : 'Send to Customer'}
              </button>
            </div>
          )}
          {s.status === 'sent' && s.next_followup_date && (
            <div className="w-full space-y-2">
              <p className="text-sm text-slate-700">
                <Bell className="w-4 h-4 inline text-amber-700 mr-1" />
                Next follow-up: <span className="text-slate-900 font-bold">{s.next_followup_date}</span>
              </p>
              <div className="flex gap-2">
                <button onClick={followupDone} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
                  Contact Made — Schedule Next
                </button>
                <button onClick={() => setShowSkip(!showSkip)} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-sm font-medium">
                  Skip Follow-up
                </button>
              </div>
              {showSkip && (
                <div className="flex gap-2 items-center">
                  <select value={skipReason} onChange={e => setSkipReason(e.target.value)}
                    className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none">
                    <option value="">Select reason...</option>
                    {['Customer declined — not interested', 'Bad experience — cooling off period', 'Already in contact',
                      'Contract signed', 'Customer went with competitor', 'Budget constraints — will revisit later',
                      'Contact no longer at company', 'Other'].map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button onClick={followupSkip} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
                    Confirm Skip
                  </button>
                </div>
              )}
            </div>
          )}
          {s.status === 'draft' && (
            <button onClick={async () => {
              try { await axios.delete(`${API}/${s.id}`); toast.success('Deleted'); onBack(); }
              catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
            }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
        {s.approved_by && (
          <p className="text-xs text-emerald-700">Approved by {s.approved_by} on {new Date(s.approved_at).toLocaleDateString()}
            {s.approval_notes && ` — "${s.approval_notes}"`}</p>
        )}
        {s.sent_at && (
          <p className="text-xs text-blue-700">
            Emailed to <span className="text-slate-900 font-medium">{s.sent_to_email}</span> on {new Date(s.sent_at).toLocaleString(undefined, { timeZone: 'America/Toronto' })}
            {s.sent_by && ` by ${s.sent_by}`}
          </p>
        )}
      </div>

      {/* Follow-up History */}
      {s.followups?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Follow-up History</h3>
          <div className="space-y-2">
            {s.followups.map((f, i) => (
              <div key={f.id || i} className={`px-3 py-2 rounded-lg text-xs ${
                f.status === 'completed' ? 'bg-emerald-50 border border-emerald-200' :
                f.status === 'skipped' ? 'bg-red-50 border border-red-200' :
                'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-slate-900 capitalize">{f.type.replace('_', ' ')}</span>
                  <span className="text-slate-500">{f.due_date}</span>
                </div>
                {f.notes && <p className="text-slate-500 mt-0.5">{f.notes}</p>}
                {f.skip_reason && <p className="text-red-600 mt-0.5">Reason: {f.skip_reason}</p>}
                {f.completed_by && <p className="text-slate-500 mt-0.5">By {f.completed_by}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
